import { useEffect } from "react";

import { Nav, Footer } from "../components/Nav";
import cover from "../images/CDM/CDM Home.png";
import structureImg from "../images/CDM/CDM Site Structure.png";
import persona1 from "../images/CDM/CDM Persona 1.png";
import persona2 from "../images/CDM/CDM Persona 2.png";
import persona3 from "../images/CDM/CDM Persona 3.png";
import moodImg from "../images/CDM/CDM Mood.png";
import wireImg from "../images/CDM/CDM Wireframes.png";
import colorImg from "../images/CDM/CDM Colors.png";
import homeImg from "../images/CDM/CDM Home.png";
import aboutImg from "../images/CDM/CDM About.png";
import vistImg from "../images/CDM/CDM Visit.png";
import ticketImg from "../images/CDM/CDM Tickets.png";
import allImg from "../images/CDM/CDM All Design.png";
import {
  ProjectInfo,
  TextSection,
  MoreSection,
} from "../components/ProjectDetails";

export default function CDM() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <>
      <Nav color="white" />
      <header id="project-cover" className="cdm">
        <h1 className="accent-text">Children's Discovery Museum</h1>
        <p>
          Playful redesign of my favorite childhood museum's outdated website!
        </p>
        <img id="cover" src={cover} alt="SMT Mockup" />
      </header>
      <main id="project-detail-main" className="cdm">
        <ProjectInfo
          role={["UI/UX Designer"]}
          team={["Solo Project"]}
          toolkit={["Figma", "Adobe Illustrator", "Miro"]}
          duration="1 Month (2023)"
        />
        <TextSection
          type="normal-text"
          header="The Problem"
          title="Outdated Current Website"
        >
          <p>
            The current website for the Children's Discovery Museum (CDM) is
            outdated and does not capture the excitement and creativity of the
            museum's interactive exhibits. This results in a less inviting
            online presence, limiting the museum's ability to attract and engage
            visitors.
          </p>
          <a
            href="https://www.cdm.org/"
            className="primary-button"
            target="_blank"
            rel="noopener noreferrer"
          >
            View Current Website
          </a>
        </TextSection>
        <TextSection
          type="callout"
          header="Solution"
          title="Modernize Outdated Website and Improve Usability"
        >
          <p>
            The goal of the redesign is to create a vibrant and engaging website
            that accurately reflects the museum's interactive environment. The
            new design will enhance usability, capture the playful spirit of the
            museum, and maintain the museum's branding.
          </p>
        </TextSection>
        <TextSection
          type="normal-text"
          header="Understanding Users"
          title="Identifying the Target Audiences"
        >
          <p>
            Based on website advertisements and age recommendations, the primary
            target audiences include:
          </p>
          <ul>
            <li>
              <strong>Parents with young children </strong>seeking engaging and
              educational experiences.
            </li>
            <li>
              <strong>Young children </strong>who benefit from interactive and
              play-based learning.
            </li>
            <li>
              <strong> Local schools </strong>organizing field trips to provide
              hands-on educational opportunities.
            </li>
          </ul>
        </TextSection>
        <TextSection
          type="normal-text"
          header="Understanding Users"
          title="Personas"
        >
          <p>
            To better understand the target audience and their needs, I created
            personas representing typical users.
          </p>
          <img
            src={persona1}
            alt="Persona 1"
            style={{ border: "solid 1px black " }}
            className="smaller-img"
          ></img>
          <img
            src={persona2}
            alt="Persona 2"
            style={{ border: "solid 1px black " }}
            className="smaller-img"
          ></img>
          <img
            src={persona3}
            alt="Persona 3"
            style={{ border: "solid 1px black " }}
            className="smaller-img"
          ></img>
        </TextSection>
        <TextSection
          type="normal-text"
          header="Understanding Users"
          title="Typical Tasks"
        >
          <p>
            By developing personas, I identified the most common tasks users
            typically perform when visiting the website.
          </p>
          <ul>
            <li>Purchasing Tickets and Membership</li>
            <li>Browsing Activities and Exhibitions</li>
            <li>Learning About the Museum</li>
          </ul>
        </TextSection>
        <TextSection
          type="normal-text"
          header="Research"
          title="Site Structure"
        >
          <p>
            Before starting the redesign, I analyzed the original site structure
            and identified the key content that needed to be included.
          </p>
          <img src={structureImg} alt="CDM Site Structure" />
          <p>
            Looking at this overwhelming diagram, we can emphasize the
            challenges users face in navigating to relevant pages. For my
            redesign, I carefully reviewed all the pages and prioritized the
            most essential ones, focusing on the{" "}
            <strong>Home page, Visit page, Ticket page, and About page.</strong>
          </p>
        </TextSection>
        <TextSection
          type="normal-text"
          header="Brainstorming"
          title="Mood Board"
        >
          <p>
            Next, I created a mood board that captures a modern and playful
            feel, while incorporating elements from the museum's original
            branding such as the museum's duck mascot.
          </p>
          <img src={moodImg} alt="CDM Mood Board" />
        </TextSection>
        <TextSection type="normal-text" header="Ideation" title="Wireframes">
          <p>
            Lastly, using Illustrator, I experimented with different page
            layouts before starting the redesign.
          </p>
          <img src={wireImg} alt="CDM Wireframes" />
        </TextSection>
        <TextSection type="callout" header="Final Design" title="Color Palette">
          <p>
            For the final redesign, I chose a color palette that preserves the
            core brand colors while updating them for a more modern and playful
            look.
          </p>
          <img src={colorImg} alt="CDM Color Palette" />
        </TextSection>

        <TextSection type="callout" header="Final Design" title="Home Page">
          <p>
            The homepage showcases a cleaner and more distinctive navigation
            design, enhancing usability and making it easier for visitors to
            find what they need. In contrast to the previous layout, which felt
            overwhelming, cluttered, and structurally inconsistent, this
            redesign introduces a more intuitive experience. Additionally, I
            incorporated playful duck footprints, inspired by Discovery Duck—the
            museum’s mascot—to bring a lively and engaging touch to the website.
          </p>
          <img src={homeImg} alt="CDM Home Page" />
        </TextSection>
        <TextSection type="callout" header="Final Design" title="Visit Page">
          <p>
            Quickly view ongoing exhibits and museum hours to start planning a
            visit! Lively duck footprints from the museum's mascot can be seen
            throughout the site to guide the users around the page. Compared to
            the previous design, this redesign offers a cleaner and more
            intuitive experience.
          </p>
          <img src={vistImg} alt="CDM Visit Page" className="smaller-img" />
        </TextSection>
        <TextSection type="callout" header="Final Design" title="Ticket Page">
          <p>
            The redesigned ticket page presents clear pricing in a clean,
            easy-to-read format, making it accessible for both parents and
            children. Unlike the previous design, where ticket information was
            buried within multiple tabs, users can now quickly access it
            directly from the Visit page for a more seamless experience.
          </p>
          <img src={ticketImg} alt="CDM Ticket Page" />
        </TextSection>
        <TextSection type="callout" header="Final Design" title="About Page">
          <p>
            Discover the museum’s mission, meet the staff, and explore ways to
            support its efforts on the About page. As a nonprofit with a
            meaningful impact on the community, this page plays a crucial role
            in connecting visitors to its purpose. The redesign focuses on
            clarity and conciseness, making it easy for users to quickly
            understand the museum’s mission and the people behind it.
          </p>
          <img src={aboutImg} alt="CDM About Page" className="smaller-img" />
        </TextSection>
        <TextSection
          type="normal-text"
          header="Final Design"
          title="All Design Pages"
        >
          <img
            src={allImg}
            className="full-width-img"
            alt="Final CDM Designs"
          />
        </TextSection>
      </main>
      <MoreSection exclude={["cdm"]} />
      <Footer />
    </>
  );
}
